import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown, MenuItem } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { ChevronDown, Clock } from 'lucide-react';

import Thumbnail from 'components/Thumbnails/Thumbnail';
import styles from './TalentCard.module.scss';
import moment from 'moment';

const TalentCard = ({ user, collaborations, onInvite }) => {
  const history = useHistory();

  if (!user) return null;

  const name = user.displayname || user.display_name || user.username;
  const role =
    (Array.isArray(user.top_roles) && user.top_roles[0]) ||
    'Expert in your pool';
  const displaySkills = user.skills?.slice(0, 3) || [];
  const profileUrl = user.links?.main_relative || `/${user.username}`;

  const getInitials = () => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleCardClick = () => {
    history.push(profileUrl);
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const hasBriefs =
    Array.isArray(collaborations) &&
    collaborations.length > 0 &&
    typeof collaborations[0] === 'object' &&
    Object.prototype.hasOwnProperty.call(collaborations[0], 'id');

  const renderInviteButton = () => {
    if (!hasBriefs) {
      return (
        <LinkContainer to={`/${user.username}/hire`} onClick={stopPropagation}>
          <Button bsStyle="primary" block className={styles.inviteButton}>
            Invite Expert
            <ChevronDown
              width={16}
              height={16}
              className={styles.chevronIcon}
            />
          </Button>
        </LinkContainer>
      );
    }

    if (collaborations.length === 1) {
      return (
        <Button
          bsStyle="primary"
          block
          className={styles.inviteButton}
          onClick={(e) => {
            stopPropagation(e);
            onInvite(collaborations[0], user);
          }}
        >
          Invite Expert
          <ChevronDown width={16} height={16} className={styles.chevronIcon} />
        </Button>
      );
    }

    return (
      <Dropdown
        id={`invite-dropdown-${user.id}`}
        onClick={stopPropagation}
        className={styles.inviteDropdown}
      >
        <Dropdown.Toggle
          bsStyle="primary"
          className={styles.inviteButton}
          noCaret
        >
          Invite Expert
          <ChevronDown width={16} height={16} className={styles.chevronIcon} />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <LinkContainer to={`/${user.username}/hire`}>
            <MenuItem eventKey="new">Invite to new project</MenuItem>
          </LinkContainer>
          <MenuItem divider />
          <MenuItem header>Invite to job:</MenuItem>
          {collaborations.map((brief) => (
            <MenuItem
              key={brief.id}
              eventKey={brief.id}
              onClick={() => onInvite(brief, user)}
            >
              {brief.text}
            </MenuItem>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <article
      className={styles.talentCard}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`View ${name}'s profile`}
    >
      <div className={styles.cardHeader}>
        <div className={styles.avatarWrapper}>
          {user.avatars && user.avatars.length > 0 ? (
            <Thumbnail
              thumbnails={user.avatars}
              className={styles.avatar}
              circle
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              <span className={styles.avatarInitials}>{getInitials()}</span>
            </div>
          )}
        </div>
        <h3 className={styles.name}>{name}</h3>
        <p className={styles.role}>{role}</p>
      </div>

      <div className={styles.skillsSection}>
        <div className={styles.skills}>
          {displaySkills.map((skill, index) => (
            <span key={index} className={styles.skillBadge}>
              {typeof skill === 'string' ? skill : skill.name}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.statsSection}>
        <div className={styles.statRow}>
          <span className={styles.statLabel}>
            <Clock width={12} height={12} />
            Status
          </span>
          <span className={`${styles.statusBadge} ${styles.statusAvailable}`}>
            {!user.available_at ||
            moment.utc(user.available_at).isBefore(moment())
              ? 'Available'
              : 'Not available'}
          </span>
        </div>
      </div>

      <div className={styles.buttonWrapper} onClick={stopPropagation}>
        {renderInviteButton()}
      </div>
    </article>
  );
};

TalentCard.propTypes = {
  user: PropTypes.object,
  collaborations: PropTypes.arrayOf(PropTypes.object),
  onInvite: PropTypes.func.isRequired,
};

export default TalentCard;
